import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Bot, RefreshCw, RotateCcw, Send, ShieldCheck, Sparkles, User as UserIcon } from "lucide-react";
import { chooseAllowedCapability, resolveAIAvailability } from "@/lib/aiSafety";

const CAPABILITIES = [
    ["rewards"],
    ["general"], ["farm_summary"], ["livestock_summary"], ["growth_observation"], ["feed_inventory"],
    ["expense_review"], ["workforce_earnings"], ["task_priority"], ["health_observation"], ["breeding_insight"], ["breeding_gestation_summary"],
];

const SUGGESTIONS = {
    farm_summary: "Summarize current farm performance and highlight the items that need attention.",
    growth_observation: "Review recent animal weights and identify growth observations that should be checked.",
    feed_inventory: "Review feed and inventory levels and flag practical reorder risks.",
    expense_review: "Review this month's expense totals for unusual patterns that deserve owner review.",
    workforce_earnings: "Explain this month's farm-wide labour cost, productivity, revenue, and net earnings without attributing revenue to individual employees.",
    task_priority: "Suggest and prioritize today's farm tasks. Do not create or change any task records.",
    health_observation: "Review current health observations and schedules, and identify cases that should be escalated to a veterinarian.",
    breeding_insight: "Review authorized breeding, gestation, and farrowing summaries and highlight observations that need attention.",
    general: "What should the farm team pay attention to today?",
};

export default function AssistantPage() {
    const { t, i18n: i18next } = useTranslation();
    const [params] = useSearchParams();
    const requestedCapability = params.get("capability") || "general";
    const [status, setStatus] = useState(null);
    const [statusError, setStatusError] = useState("");
    const [history, setHistory] = useState([]);
    const [input, setInput] = useState("");
    const [capability, setCapability] = useState(requestedCapability);
    const [loading, setLoading] = useState(false);
    const [lastFailed, setLastFailed] = useState(null);
    const bottomRef = useRef(null);

    useEffect(() => {
        api.get("/ai/status")
            .then(({ data }) => {
                setStatus(data);
                const allowed = data.capabilities || [];
                const assistantAllowed = CAPABILITIES.map(([value]) => value).filter((value) => allowed.includes(value));
                setCapability(chooseAllowedCapability(requestedCapability, assistantAllowed));
            })
            .catch((error) => setStatusError(formatApiErrorDetail(error.response?.data?.detail) || t("ai_katulong.status_unavailable")));
    }, [requestedCapability]);
    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [history, loading]);

    const allowedCapabilities = CAPABILITIES.filter(([value]) => status?.capabilities?.includes(value));
    const availability = resolveAIAvailability(status, !!statusError);
    const available = availability.available && allowedCapabilities.length > 0;

    const send = async (event, retryMessage = "") => {
        event?.preventDefault?.();
        const message = (retryMessage || input).trim();
        if (!message || !available) return;
        setInput("");
        setLoading(true);
        setLastFailed(null);
        const pendingId = `pending-${Date.now()}`;
        setHistory(rows => [...rows, { id: pendingId, message, pending: true }]);
        try {
            const language = i18next.resolvedLanguage === "tl" ? "tl" : "en";
            const { data } = await api.post("/ai/assist", { message, capability, language });
            setHistory(rows => rows.map(row => row.id === pendingId ? { ...row, pending: false, ...data } : row));
        } catch (error) {
            const detail = formatApiErrorDetail(error.response?.data?.detail) || t("ai_katulong.unavailable");
            setHistory(rows => rows.map(row => row.id === pendingId ? { ...row, pending: false, error: detail } : row));
            setLastFailed(message);
        } finally {
            setLoading(false);
        }
    };

    const starters = [
        ["dashboard", "farm_summary"], ["attention", "general"], ["farm", "farm_summary"],
        ["livestock", "livestock_summary"], ["inventory", "feed_inventory"], ["tasks", "task_priority"],
        ["breeding", "breeding_gestation_summary"], ["finance", "expense_review"], ["workforce", "workforce_earnings"],
        ["attendance", "general"], ["events", "general"],
    ].filter(([, value]) => status?.capabilities?.includes(value));

    useEffect(() => {
        const prompt = params.get("prompt");
        if (prompt) setInput(prompt.slice(0, 2000));
    }, [params]);

    return (
        <div className="space-y-5 min-h-[calc(100vh-5rem)] flex flex-col aj-assistant-page" data-testid="assistant-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground grid place-items-center"><Sparkles className="w-6 h-6" /></div>
                    <div><div className="tiny-label">{t("ai_katulong.subtitle")}</div><h1 className="text-3xl font-bold tracking-tight">{t("ai_katulong.title")}</h1></div>
                </div>
                <div className="flex items-center gap-2"><div className="inline-flex items-center gap-2 text-xs text-muted-foreground"><ShieldCheck className="w-4 h-4" />{t("ai_katulong.read_only")}</div><Button variant="outline" size="sm" onClick={() => { setHistory([]); setInput(""); setLastFailed(null); }} data-testid="ai-new-conversation"><RotateCcw className="w-4 h-4 mr-2" />{t("ai_katulong.new_conversation")}</Button></div>
            </div>

            {(statusError || (status && !available)) && (
                <Card className="p-4 border-amber-300 bg-amber-50 text-amber-950" data-testid="ai-unavailable">
                    <div className="flex gap-3"><AlertTriangle className="w-5 h-5 shrink-0" /><div>
                        <div className="font-semibold">{t("ai_katulong.unavailable")}</div>
                        <div className="text-sm mt-1">{statusError || t("ai_katulong.unavailable_help")}</div>
                    </div></div>
                </Card>
            )}

            <Card className="flex-1 flex flex-col overflow-hidden min-h-[34rem] aj-assistant-console">
                <div className="p-4 border-b flex flex-col sm:flex-row gap-3 sm:items-center">
                    {allowedCapabilities.length > 0 ? <Select value={capability} onValueChange={(value) => { setCapability(value); setInput(SUGGESTIONS[value] || ""); }} disabled={!available}>
                        <SelectTrigger className="sm:w-72" data-testid="ai-capability-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{allowedCapabilities.map(([value]) => <SelectItem key={value} value={value}>{t(value === "rewards" ? "rewards.title" : `ai_katulong.capabilities.${value}`)}</SelectItem>)}</SelectContent>
                    </Select> : <div className="text-sm text-amber-700" data-testid="ai-no-capabilities">{t("ai_katulong.no_authorized_capabilities")}</div>}
                    <div className="text-xs text-muted-foreground">{t("ai_katulong.advisory")}</div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5" data-testid="chat-history">
                    {history.length === 0 && (
                        <div className="text-center max-w-xl mx-auto mt-10">
                            <Bot className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                            <div className="font-semibold text-lg">{t("ai_katulong.empty_title")}</div>
                            <p className="text-muted-foreground text-sm mt-2">{t("ai_katulong.empty_help")}</p>
                            <div className="grid sm:grid-cols-2 gap-2 mt-5" data-testid="ai-starter-actions">{starters.map(([key, value]) => <Button key={key} type="button" variant="outline" className="h-auto py-3 whitespace-normal" disabled={!available} onClick={() => { setCapability(value); setInput(t(`ai_katulong.starters.${key}`)); }}>{t(`ai_katulong.starters.${key}`)}</Button>)}</div>
                        </div>
                    )}
                    {history.map((entry) => <div key={entry.id} className="space-y-3">
                        <div className="flex gap-3 justify-end"><div className="max-w-[85%] rounded-2xl rounded-tr-sm bg-primary text-primary-foreground px-4 py-3 text-sm whitespace-pre-wrap">{entry.message}</div><div className="w-8 h-8 rounded-full bg-accent grid place-items-center shrink-0"><UserIcon className="w-4 h-4" /></div></div>
                        <div className="flex gap-3"><div className="w-8 h-8 rounded-full bg-primary text-primary-foreground grid place-items-center shrink-0"><Bot className="w-4 h-4" /></div><div className="max-w-[85%] rounded-2xl rounded-tl-sm border border-primary/20 bg-[#fffdf5] px-4 py-3 text-[#173d2a] shadow-sm" data-testid="ai-response-card">
                            {entry.pending && <div className="text-sm text-[#496b59]" data-testid="ai-loading">{t("ai_katulong.loading")}</div>}
                            {entry.error && <div className="text-sm text-destructive" data-testid="ai-error">{entry.error}</div>}
                            {entry.response && <div className="text-sm leading-relaxed whitespace-pre-wrap break-words" data-testid="ai-response">{entry.response}</div>}
                            {entry.disclaimer && <div className="mt-3 border-t border-primary/15 pt-3 text-xs text-[#496b59]">{entry.disclaimer}</div>}
                            {entry.sources?.length > 0 && <div className="mt-3 flex flex-wrap gap-1" data-testid="ai-sources">{entry.sources.map(source => <span key={source.category} className="max-w-full break-words rounded-full border border-primary/25 bg-white px-2 py-1 text-xs text-[#214d35]">{source.label} ({source.record_count})</span>)}</div>}
                        </div></div>
                    </div>)}
                    <div ref={bottomRef} />
                </div>
                <form onSubmit={send} className="p-4 border-t flex gap-2">
                    <Input value={input} onChange={event => setInput(event.target.value)} maxLength={2000} placeholder={t("ai_katulong.placeholder")} className="h-12 flex-1" disabled={!available || loading} data-testid="chat-input" />
                    {lastFailed ? <Button type="button" variant="outline" className="h-12" onClick={(event) => send(event, lastFailed)} disabled={loading} data-testid="chat-retry-btn"><RefreshCw className="w-4 h-4 mr-2" />{t("ai_katulong.retry")}</Button> : null}
                    <Button type="submit" className="h-12" aria-label={t("ai_katulong.send")} disabled={!available || loading || !input.trim()} data-testid="chat-send-btn"><Send className="w-4 h-4" /></Button>
                </form>
            </Card>
            <p className="text-xs text-muted-foreground">{t("ai_katulong.advisory")}</p>
        </div>
    );
}
